<template>
  <div class="shift-details-container">
    <!-- Header -->
    <Header :title="shift ? `Détails du shift: ${shift.name}` : 'Détails du shift'">
      <template #icon>
        <span class="material-icons">schedule</span>
      </template>
      <template #actions>
        <router-link to="/shifts" class="back-button">
          <span class="material-icons">arrow_back</span>
          Retour à la liste
        </router-link>
      </template>
    </Header>

    <div class="content-container" v-if="loading">
      <div class="loading-indicator">
        <div class="spinner"></div>
        <p>Chargement des détails du shift...</p>
      </div>
    </div>

    <div class="content-container" v-else-if="!shift">
      <div class="no-data-message">
        <span class="material-icons">error_outline</span>
        <p>Shift non trouvé</p>
        <router-link to="/shifts" class="btn btn-primary">
          Retour à la liste des shifts
        </router-link>
      </div>
    </div>

    <div class="content-container" v-else>
      <!-- Système d'onglets -->
      <div class="tabs-container">
        <div class="tabs">
          <button class="tab-button" :class="{ active: activeTab === 'general' }" @click="activeTab = 'general'">
            <span class="material-icons">info</span> Informations générales
          </button>
          <button class="tab-button" :class="{ active: activeTab === 'jours' }" @click="activeTab = 'jours'">
            <span class="material-icons">calendar_today</span> Horaires par jour
          </button>
          <button class="tab-button" :class="{ active: activeTab === 'stats' }" @click="activeTab = 'stats'">
            <span class="material-icons">bar_chart</span> Statistiques
          </button>
          <button class="tab-button" :class="{ active: activeTab === 'pointages' }" @click="activeTab = 'pointages'">
            <span class="material-icons">fact_check</span> Pointages
          </button>
        </div>
      </div>
      
      <!-- Contenu de l'onglet Informations générales -->
      <div class="details-card" v-if="activeTab === 'general'">
        <div class="card-header">
          <h2>Informations générales</h2>
          <div class="header-actions">
            <button @click="editShift" class="btn btn-primary">
              <span class="material-icons">edit</span>
              Modifier
            </button>
          </div>
        </div>
        
        <div class="card-body">
          <div class="shift-header">
            <div class="shift-code" :style="{ backgroundColor: shift.color }">
              {{ shift.code }}
            </div>
            <div class="shift-title">
              <h3>{{ shift.name }}</h3>
              <span :class="['status-badge', shift.active ? 'active' : 'inactive']">
                <span class="material-icons">{{ shift.active ? 'check_circle' : 'cancel' }}</span>
                {{ shift.active ? 'Actif' : 'Inactif' }}
              </span>
            </div>
          </div>
          
          <div class="details-grid">
            <div class="detail-item">
              <span class="label">Description</span>
              <span class="value">{{ shift.description || 'Non spécifiée' }}</span>
            </div>
            
            <div class="detail-item">
              <span class="label">Heure de début</span>
              <span class="value time">
                <span class="material-icons">access_time</span>
                {{ formatTime(shift.heure_debut) }}
              </span>
            </div>
            
            <div class="detail-item">
              <span class="label">Heure de fin</span>
              <span class="value time">
                <span class="material-icons">access_time</span>
                {{ formatTime(shift.heure_fin) }}
              </span>
            </div>
            
            <div class="detail-item">
              <span class="label">Pause début</span>
              <span class="value time">
                <span class="material-icons">free_breakfast</span>
                {{ formatTime(shift.pause_debut) || 'Non définie' }}
              </span>
            </div>
            
            <div class="detail-item">
              <span class="label">Pause fin</span>
              <span class="value time">
                <span class="material-icons">free_breakfast</span>
                {{ formatTime(shift.pause_fin) || 'Non définie' }}
              </span>
            </div>
            
            <div class="detail-item">
              <span class="label">Tolérance de retard</span>
              <span class="value">{{ shift.tolerance_retard }} minutes</span>
            </div>
            
            <div class="detail-item">
              <span class="label">Coefficient</span>
              <span class="value">{{ shift.coefficient }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Horaires par jour -->
      <div class="details-card" v-if="activeTab === 'jours'">
        <div class="card-header">
          <h2>Horaires par jour</h2>
          <div class="header-actions">
            <button @click="applyToAllDays" class="btn btn-primary">
              <span class="material-icons">content_copy</span>
              Appliquer à tous les jours
            </button>
          </div>
        </div>
        
        <div class="card-body">
          <div v-if="loadingJourShifts" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des horaires par jour...</p>
          </div>
          
          <div v-else class="jour-shifts-container">
            <!-- Tableau des horaires par jour -->
            <div class="table-container">
              <table class="jour-shifts-table">
                <thead>
                  <tr>
                    <th>Jour</th>
                    <th>Actif</th>
                    <th>Heure de début</th>
                    <th>Heure de fin</th>
                    <th>Tolérance (min)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="jour in joursDeLaSemaine" :key="jour.jour">
                    <td class="jour-nom">{{ jour.nom }}</td>
                    <td>
                      <label class="toggle-switch">
                        <input type="checkbox" v-model="jour.est_actif" @change="updateJourShift(jour)">
                        <span class="toggle-slider"></span>
                      </label>
                    </td>
                    <td>
                      <input 
                        type="time" 
                        v-model="jour.heure_debut_override" 
                        :placeholder="shift.heure_debut"
                        :disabled="!jour.est_actif"
                        @change="updateJourShift(jour)"
                        class="time-input"
                      />
                      <span v-if="!jour.heure_debut_override && jour.est_actif" class="default-value">
                        {{ formatTime(shift.heure_debut) }} (défaut)
                      </span>
                    </td>
                    <td>
                      <input 
                        type="time" 
                        v-model="jour.heure_fin_override" 
                        :placeholder="shift.heure_fin"
                        :disabled="!jour.est_actif"
                        @change="updateJourShift(jour)"
                        class="time-input"
                      />
                      <span v-if="!jour.heure_fin_override && jour.est_actif" class="default-value">
                        {{ formatTime(shift.heure_fin) }} (défaut)
                      </span>
                    </td>
                    <td>
                      <input 
                        type="number" 
                        v-model.number="jour.tolerance_retard_override" 
                        :placeholder="shift.tolerance_retard"
                        :disabled="!jour.est_actif"
                        @change="updateJourShift(jour)"
                        class="number-input"
                        min="0"
                      />
                      <span v-if="jour.tolerance_retard_override === null && jour.est_actif" class="default-value">
                        {{ shift.tolerance_retard }} min (défaut)
                      </span>
                    </td>
                    <td>
                      <button 
                        @click="resetJourShift(jour)" 
                        class="action-btn" 
                        title="Réinitialiser aux valeurs par défaut"
                        :disabled="!jour.est_actif"
                      >
                        <span class="material-icons">restore</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Visualisation hebdomadaire -->
            <div class="weekly-calendar">
              <h3>Aperçu hebdomadaire</h3>
              <div class="calendar-container">
                <div class="time-axis">
                  <div v-for="hour in 24" :key="hour-1" class="hour-marker">
                    {{ hour-1 }}:00
                  </div>
                </div>
                
                <div class="days-container">
                  <div v-for="jour in joursDeLaSemaine" :key="jour.jour" class="day-column">
                    <div class="day-header">{{ jour.nom.substring(0, 3) }}</div>
                    <div class="day-content">
                      <div 
                        v-if="jour.est_actif" 
                        class="shift-bar"
                        :style="getShiftBarStyle(jour)"
                      >
                        {{ shift.code }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Statistiques du shift -->
      <div class="details-card" v-if="activeTab === 'stats'">
        <div class="card-header">
          <h2>Statistiques de pointage</h2>
          <div class="header-actions">
            <div class="date-range-picker">
              <input type="date" v-model="statsDateRange.debut" @change="loadShiftStats" />
              <span>à</span>
              <input type="date" v-model="statsDateRange.fin" @change="loadShiftStats" />
            </div>
          </div>
        </div>
        
        <div class="card-body">
          <div v-if="loadingStats" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des statistiques...</p>
          </div>
          
          <div v-else-if="!shiftStats" class="no-data-message">
            <span class="material-icons">bar_chart</span>
            <p>Aucune statistique disponible pour la période sélectionnée</p>
          </div>
          
          <div v-else class="stats-container">
            <!-- Résumé des statistiques -->
            <div class="stats-summary">
              <div class="stat-card">
                <div class="stat-value">{{ shiftStats.resume.total }}</div>
                <div class="stat-label">Total des pointages</div>
              </div>
              
              <div class="stat-card">
                <div class="stat-value">{{ shiftStats.resume.a_l_heure }}</div>
                <div class="stat-label">À l'heure</div>
              </div>
              
              <div class="stat-card">
                <div class="stat-value">{{ shiftStats.resume.en_retard }}</div>
                <div class="stat-label">En retard</div>
              </div>
              
              <div class="stat-card">
                <div class="stat-value">{{ shiftStats.resume.retard_moyen_minutes }} min</div>
                <div class="stat-label">Retard moyen</div>
              </div>
            </div>
            
            <!-- Graphique à intégrer ici -->
            <div class="stats-chart">
              <p>Graphique des statistiques à implémenter</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Liste des pointages associés -->
      <div class="details-card" v-if="activeTab === 'pointages'">
        <div class="card-header">
          <h2>Pointages associés</h2>
          <div class="header-actions">
            <div class="filters">
              <select v-model="pointageFilters.status" @change="loadPointages">
                <option value="">Tous les statuts</option>
                <option value="A_LHEURE">À l'heure</option>
                <option value="RETARD">En retard</option>
                <option value="HORS_PLAGE">Hors plage</option>
              </select>
              
              <input type="date" v-model="pointageFilters.debut" @change="loadPointages" />
              <span>à</span>
              <input type="date" v-model="pointageFilters.fin" @change="loadPointages" />
            </div>
          </div>
        </div>
        
        <div class="card-body">
          <div v-if="loadingPointages" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des pointages...</p>
          </div>
          
          <div v-else-if="!pointages.length" class="no-data-message">
            <span class="material-icons">fingerprint</span>
            <p>Aucun pointage trouvé pour ce shift avec les filtres actuels</p>
          </div>
          
          <div v-else class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Personnel</th>
                  <th>Date</th>
                  <th>Heure</th>
                  <th>Motif</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pointage in pointages" :key="pointage.id">
                  <td>{{ pointage.personnel?.nom }} {{ pointage.personnel?.prenom }}</td>
                  <td>{{ formatDate(pointage.date) }}</td>
                  <td>{{ formatTime(pointage.heure_chaine) }}</td>
                  <td>{{ pointage.motif }}</td>
                  <td>
                    <span :class="['status-badge', getStatusClass(pointage.validation?.status)]">
                      {{ formatStatus(pointage.validation?.status) }}
                    </span>
                  </td>
                  <td class="actions">
                    <button @click="viewPointageDetails(pointage)" class="action-btn" title="Voir les détails">
                      <span class="material-icons">visibility</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            
            <!-- Pagination -->
            <div class="pagination" v-if="pagination.totalPages > 1">
              <button 
                :disabled="pagination.page === 1" 
                @click="changePage(pagination.page - 1)"
                class="page-btn"
              >
                <span class="material-icons">chevron_left</span>
              </button>
              
              <span class="page-info">Page {{ pagination.page }} sur {{ pagination.totalPages }}</span>
              
              <button 
                :disabled="pagination.page === pagination.totalPages" 
                @click="changePage(pagination.page + 1)"
                class="page-btn"
              >
                <span class="material-icons">chevron_right</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal pour éditer le shift -->
    <ShiftForm 
      v-if="showEditModal" 
      :shift="shift"
      @close="closeEditModal"
      @save="onShiftSaved"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/services/api';
import Header from '@/components/Header.vue';
import ShiftForm from '@/components/pointage/ShiftForm.vue';

const route = useRoute();
const router = useRouter();
const shiftId = computed(() => route.params.id);

// États
const shift = ref(null);
const loading = ref(true);
const showEditModal = ref(false);
const activeTab = ref('general');

// États pour les statistiques
const shiftStats = ref(null);
const loadingStats = ref(false);
const statsDateRange = reactive({
  debut: new Date().toISOString().split('T')[0],
  fin: new Date().toISOString().split('T')[0]
});

// États pour les JourShift
const joursDeLaSemaine = ref([
  { nom: 'Lundi', jour: 'lundi', est_actif: false, heure_debut_override: null, heure_fin_override: null, tolerance_retard_override: null, id: null },
  { nom: 'Mardi', jour: 'mardi', est_actif: false, heure_debut_override: null, heure_fin_override: null, tolerance_retard_override: null, id: null },
  { nom: 'Mercredi', jour: 'mercredi', est_actif: false, heure_debut_override: null, heure_fin_override: null, tolerance_retard_override: null, id: null },
  { nom: 'Jeudi', jour: 'jeudi', est_actif: false, heure_debut_override: null, heure_fin_override: null, tolerance_retard_override: null, id: null },
  { nom: 'Vendredi', jour: 'vendredi', est_actif: false, heure_debut_override: null, heure_fin_override: null, tolerance_retard_override: null, id: null },
  { nom: 'Samedi', jour: 'samedi', est_actif: false, heure_debut_override: null, heure_fin_override: null, tolerance_retard_override: null, id: null },
  { nom: 'Dimanche', jour: 'dimanche', est_actif: false, heure_debut_override: null, heure_fin_override: null, tolerance_retard_override: null, id: null }
]);
const loadingJourShifts = ref(false);

// États pour les pointages
const pointages = ref([]);
const loadingPointages = ref(false);
const pointageFilters = reactive({
  status: '',
  debut: new Date().toISOString().split('T')[0],
  fin: new Date().toISOString().split('T')[0],
  page: 1,
  limit: 10
});

const pagination = reactive({
  page: 1,
  limit: 10,
  totalPages: 0,
  totalItems: 0
});

// Chargement initial
onMounted(async () => {
  await loadShift();
  await loadShiftStats();
  await loadPointages();
});

// Fonctions pour charger les données
async function loadShift() {
  loading.value = true;
  try {
    const response = await api.get(`/api/parametres/shifts/${shiftId.value}`);
    shift.value = response.data.data;
  } catch (error) {
    console.error('Erreur lors du chargement du shift:', error);
    shift.value = null;
  } finally {
    loading.value = false;
  }
}

async function loadShiftStats() {
  loadingStats.value = true;
  try {
    const response = await api.get(`/pointage/shift/${shiftId.value}/stats`, {
      params: {
        debut: statsDateRange.debut,
        fin: statsDateRange.fin
      }
    });
    shiftStats.value = response.data.data;
  } catch (error) {
    console.error('Erreur lors du chargement des statistiques:', error);
    shiftStats.value = null;
  } finally {
    loadingStats.value = false;
  }
}

async function loadPointages() {
  loadingPointages.value = true;
  try {
    const response = await api.get(`/pointage/shift/${shiftId.value}`, {
      params: {
        debut: pointageFilters.debut,
        fin: pointageFilters.fin,
        status: pointageFilters.status || undefined,
        page: pagination.page,
        limit: pagination.limit
      }
    });
    pointages.value = response.data.data.rows;
    pagination.totalPages = response.data.data.pagination.totalPages;
    pagination.totalItems = response.data.data.pagination.totalItems;
  } catch (error) {
    console.error('Erreur lors du chargement des pointages:', error);
    pointages.value = [];
  } finally {
    loadingPointages.value = false;
  }
}

// Fonctions d'action
function editShift() {
  showEditModal.value = true;
}

function closeEditModal() {
  showEditModal.value = false;
}

function onShiftSaved(savedShift) {
  shift.value = savedShift;
  closeEditModal();
}

function changePage(page) {
  pagination.page = page;
  loadPointages();
}

function viewPointageDetails(pointage) {
  // Implémenter la navigation vers les détails du pointage
  console.log('Voir détails du pointage:', pointage);
}

// Fonctions utilitaires
function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function formatTime(timeString) {
  if (!timeString) return '';
  return timeString.substring(0, 5); // Format HH:MM
}

function formatStatus(status) {
  switch (status) {
    case 'A_LHEURE': return 'À l\'heure';
    case 'RETARD': return 'En retard';
    case 'HORS_PLAGE': return 'Hors plage';
    case 'NON_DEFINI': return 'Non défini';
    default: return status || 'Inconnu';
  }
}

function getStatusClass(status) {
  switch (status) {
    case 'A_LHEURE': return 'success';
    case 'RETARD': return 'warning';
    case 'HORS_PLAGE': return 'danger';
    case 'NON_DEFINI': return 'neutral';
    default: return 'neutral';
  }
}

// Fonctions pour les JourShift
async function loadJourShifts() {
  try {
    loadingJourShifts.value = true;
    let apiData = [];
    
    try {
      //api/parametres/shifts/{shiftId}/day-configurations
      const response = await api.get(`/api/parametres/shifts/${shiftId.value}/day-configurations`);
      apiData = response.data.data || [];
      // console.log('Données JourShift reçues:', apiData);
    } catch (apiError) {
      console.warn('API JourShift non disponible, utilisation des valeurs par défaut:', apiError);
    }
    
    // Mettre à jour les jours existants avec les données de l'API si disponibles
    joursDeLaSemaine.value.forEach(jour => {
      const jourShift = apiData.find(js => js.jour === jour.jour);
      
      if (jourShift) {
        // Mettre à jour avec les données de l'API
        jour.est_actif = jourShift.est_actif === 1 || false;
        jour.heure_debut_override = jourShift.heure_debut_override || null;
        jour.heure_fin_override = jourShift.heure_fin_override || null;
        jour.tolerance_retard_override = jourShift.tolerance_retard_override || null;
        jour.id = jourShift.id || null;
      }
    });
    
    console.log('Jours de la semaine mis à jour:', joursDeLaSemaine.value);
  } catch (error) {
    console.error('Erreur lors du chargement des horaires par jour:', error);
  } finally {
    loadingJourShifts.value = false;
  }
}

async function updateJourShift(jour) {
  try {
    const payload = {
      jour: jour.jour,
      shift_id: shiftId.value,
      est_actif: jour.est_actif,
      heure_debut_override: jour.heure_debut_override,
      heure_fin_override: jour.heure_fin_override,
      tolerance_retard_override: jour.tolerance_retard_override
    };
    
    if (jour.id) {
      // Mise à jour d'un JourShift existant
      await api.put(`/api/parametres/shifts/jour`, payload);
    } else if (jour.est_actif) {
      // Création d'un nouveau JourShift (seulement si actif)
      const response = await api.post('/api/parametres/shifts/jour', payload);
      jour.id = response.data.id;
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour des horaires:', error);
    jour.est_actif = !jour.est_actif; // Rétablir l'état précédent en cas d'erreur
  }
}

function resetJourShift(jour) {
  jour.heure_debut_override = null;
  jour.heure_fin_override = null;
  jour.tolerance_retard_override = null;
  updateJourShift(jour);
}

function applyToAllDays() {
  // Trouver le premier jour actif pour servir de modèle
  const jourModele = joursDeLaSemaine.value.find(j => j.est_actif);
  
  if (!jourModele) {
    console.warn('Aucun jour actif trouvé pour servir de modèle');
    return;
  }
  
  // Appliquer les paramètres du modèle à tous les autres jours
  joursDeLaSemaine.value.forEach(jour => {
    if (jour.jour !== jourModele.jour) {
      jour.est_actif = true;
      jour.heure_debut_override = jourModele.heure_debut_override;
      jour.heure_fin_override = jourModele.heure_fin_override;
      jour.tolerance_retard_override = jourModele.tolerance_retard_override;
      updateJourShift(jour);
    }
  });
}

function getHeureDebut(jour) {
  return jour.heure_debut_override || shift.value.heure_debut;
}

function getHeureFin(jour) {
  return jour.heure_fin_override || shift.value.heure_fin;
}

function getShiftBarStyle(jour) {
  const heureDebut = getHeureDebut(jour);
  const heureFin = getHeureFin(jour);
  
  // Convertir les heures en pourcentages pour le positionnement CSS
  const startHour = parseInt(heureDebut.split(':')[0]);
  const startMinute = parseInt(heureDebut.split(':')[1]);
  const startPercentage = (startHour + startMinute / 60) * 100 / 24;
  
  const endHour = parseInt(heureFin.split(':')[0]);
  const endMinute = parseInt(heureFin.split(':')[1]);
  const endPercentage = (endHour + endMinute / 60) * 100 / 24;
  
  const height = endPercentage - startPercentage;
  
  return {
    top: `${startPercentage}%`,
    height: `${height}%`,
    backgroundColor: shift.value.color
  };
}

// Appel des fonctions de chargement au montage du composant
onMounted(() => {
  // Charger d'abord le shift principal
  loadShift().then(() => {
    // Puis charger les JourShifts une fois que le shift est disponible
    if (shift.value) {
      console.log('Shift chargé, chargement des JourShifts');
      loadJourShifts();
    } else {
      console.error('Impossible de charger les JourShifts: shift non disponible');
    }
  });
  
  // Charger les autres données en parallèle
  loadShiftStats();
  loadPointages();
});
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.shift-details-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content-container {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--turquoise-primary, #30C7B5);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.back-button:hover {
  color: var(--turquoise-deep, #155751);
  transform: translateX(-3px);
}

.back-button .material-icons {
  margin-right: 2px;
}

.details-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  color: white;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.card-body {
  padding: 20px;
}

.shift-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.shift-code {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 8px;
  color: white;
  font-weight: bold;
  font-size: 18px;
  margin-right: 15px;
}

.shift-title {
  display: flex;
  flex-direction: column;
}

.shift-title h3 {
  margin: 0 0 5px;
  font-size: 24px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 5px;
}

.value {
  font-size: 16px;
  color: #111827;
}

.value.time {
  display: flex;
  align-items: center;
  gap: 5px;
}

.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.stat-card {
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: var(--turquoise-primary);
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th, .data-table td {
  padding: 12px 15px;
  text-align: left;
}

.data-table th {
  background-color: #f9fafb;
  font-weight: 500;
  color: #374151;
}

.data-table tr {
  border-bottom: 1px solid #e5e7eb;
}

.data-table tr:last-child {
  border-bottom: none;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background-color: var(--turquoise-pale);
  color: var(--turquoise-deep);
}

.status-badge.inactive {
  background-color: #f3f4f6;
  color: #6b7280;
}

.status-badge.success {
  background-color: #d1fae5;
  color: #065f46;
}

.status-badge.warning {
  background-color: #fef3c7;
  color: #92400e;
}

.status-badge.danger {
  background-color: #fee2e2;
  color: #b91c1c;
}

.status-badge.neutral {
  background-color: #f3f4f6;
  color: #6b7280;
}

.actions {
  display: flex;
  gap: 5px;
}

.action-btn {
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #374151;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  transition: background-color 0.2s;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
}

.btn-primary:hover {
  background-color: var(--turquoise-dark);
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid var(--turquoise-primary);
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Styles pour le système d'onglets */
.tabs-container {
  margin-bottom: 20px;
}

.tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
  padding-bottom: 1px;
}

.tab-button {
  padding: 10px 16px;
  border: none;
  background: none;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  white-space: nowrap;
}

.tab-button .material-icons {
  font-size: 18px;
}

.tab-button.active {
  color: var(--turquoise-primary);
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: var(--turquoise-primary);
}

.tab-button:hover:not(.active) {
  color: #374151;
  background-color: #f9fafb;
}

/* Styles pour la section JourShift */
.jour-shifts-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.jour-shifts-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.jour-shifts-table th, .jour-shifts-table td {
  padding: 10px;
  text-align: left;
  vertical-align: middle;
}

.jour-shifts-table th {
  background-color: #f9fafb;
  font-weight: 500;
  color: #374151;
}

.jour-shifts-table tr {
  border-bottom: 1px solid #e5e7eb;
}

.jour-shifts-table tr:last-child {
  border-bottom: none;
}

.jour-nom {
  font-weight: 500;
}

.time-input, .number-input {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  width: 100px;
}

.default-value {
  color: #6b7280;
  font-size: 12px;
  margin-left: 5px;
  display: block;
  margin-top: 3px;
}

/* Toggle switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: var(--turquoise-primary);
}

input:checked + .toggle-slider:before {
  transform: translateX(24px);
}

/* Visualisation hebdomadaire */
.weekly-calendar {
  margin-top: 30px;
}

.weekly-calendar h3 {
  margin-bottom: 15px;
  font-size: 18px;
  color: #374151;
}

.calendar-container {
  display: flex;
  height: 400px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.time-axis {
  width: 50px;
  background-color: #f9fafb;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 0;
}

.hour-marker {
  font-size: 11px;
  color: #6b7280;
  text-align: center;
  height: 16px;
  line-height: 16px;
}

.days-container {
  display: flex;
  flex: 1;
}

.day-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e7eb;
  position: relative;
}

.day-column:last-child {
  border-right: none;
}

.day-header {
  padding: 8px;
  text-align: center;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 500;
  font-size: 14px;
}

.day-content {
  flex: 1;
  position: relative;
}

.shift-bar {
  position: absolute;
  left: 5px;
  right: 5px;
  border-radius: 4px;
  color: white;
  font-weight: 500;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #6b7280;
}

.no-data-message .material-icons {
  font-size: 48px;
  margin-bottom: 15px;
  color: var(--turquoise-very-light);
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 10px;
}

.page-btn {
  border: none;
  background-color: white;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn:not(:disabled):hover {
  background-color: #f3f4f6;
}

.page-info {
  font-size: 14px;
  color: #6b7280;
}

.date-range-picker {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-range-picker input {
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.filters {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filters select, .filters input {
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}
</style>
